const { useEffect, useState, useRef } = React;

function HowItWorks() {
    const [isVisible, setIsVisible] = useState(false);
    const sectionRef = useRef(null);

    useEffect(() => {
        const observer = new IntersectionObserver(
            ([entry]) => {
                if (entry.isIntersecting) {
                    setIsVisible(true);
                }
            },
            { threshold: 0.2 }
        );

        if (sectionRef.current) {
            observer.observe(sectionRef.current);
        }

        return () => observer.disconnect();
    }, []);

    useEffect(() => {
        if (isVisible && typeof lucide !== 'undefined' && lucide.createIcons) {
            lucide.createIcons();
        }
    }, [isVisible]);

    const steps = [
        {
            icon: 'search',
            title: 'Discover',
            description: 'Search by location, price, property type, and amenities. Use smart filters to find your perfect property in seconds.',
            color: 'from-blue-700 to-blue-500',
        },
        {
            icon: 'calendar-check',
            title: 'Book',
            description: 'Schedule physical viewings instantly with verified realtors. Choose your preferred date and time without any hassle.',
            color: 'from-blue-600 to-emerald-500',
        },
        {
            icon: 'handshake',
            title: 'Connect',
            description: 'Meet verified realtors, compare properties, and secure your next home with confidence. Direct communication made easy.',
            color: 'from-emerald-500 to-emerald-300',
        },
    ];

    return (
        <section id="how-it-works" ref={sectionRef} className="section-shell relative py-20 sm:py-24 px-4 sm:px-6 lg:px-8 bg-transparent overflow-hidden">
            {/* Background Elements */}
            <div className="absolute inset-0 opacity-25">
                <div className="absolute top-1/4 left-1/4 w-96 h-96 bg-blue-200 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
                <div className="absolute bottom-1/4 right-1/4 w-96 h-96 bg-blue-100 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
            </div>

            <div className="max-w-6xl mx-auto relative z-10">
                {/* Section Header */}
                <div className={`text-center mb-16 transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <div className="section-kicker mx-auto mb-4 w-fit">How it works</div>
                    <h2 className="text-4xl sm:text-5xl font-bold mb-6 text-gray-900 section-title">
                        How <span className="bg-gradient-to-r from-blue-600 to-blue-400 bg-clip-text text-transparent">BEASMAR</span> Works
                    </h2>
                    <p className="text-lg sm:text-xl text-gray-600 max-w-2xl mx-auto">
                        Three simple steps to discover listings, book viewings, and work with verified realtors.
                    </p>
                    <div className="w-20 h-1 bg-gradient-to-r from-blue-600 to-blue-400 mx-auto mt-8"></div>
                </div>

                {/* Cards Grid */}
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-10">
                    {steps.map((step, index) => (
                        <div
                            key={step.title}
                            className={`group relative transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
                            style={{ transitionDelay: `${(index + 1) * 160}ms` }}
                        >
                            <div className="relative h-full">
                                <div className="absolute inset-0 bg-gradient-to-br from-blue-600/15 to-blue-400/5 rounded-2xl blur-2xl opacity-0 group-hover:opacity-100 transition-all duration-500"></div>

                                <div className="relative h-full surface-card p-7 hover:border-blue-300/70 transition-all duration-500 group-hover:shadow-2xl group-hover:shadow-blue-500/10">
                                    <div className="flex items-center justify-between mb-6">
                                        <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full text-xs font-semibold bg-white/70 border border-gray-200 text-gray-700">
                                            Step {index + 1}
                                        </div>
                                        <div className={`h-9 w-9 rounded-xl bg-gradient-to-br ${step.color} flex items-center justify-center text-white shadow-sm`}>
                                            <i data-lucide={step.icon} className="w-5 h-5"></i>
                                        </div>
                                    </div>

                                    <h3 className="text-xl font-bold text-gray-900 mb-3">{step.title}</h3>
                                    <p className="text-gray-600 leading-relaxed">{step.description}</p>

                                    <div className={`absolute bottom-0 left-0 right-0 h-1 bg-gradient-to-r ${step.color} rounded-b-2xl`}></div>
                                </div>
                            </div>
                        </div>
                    ))}
                </div>

                {/* Bottom CTA */}
                <div className={`text-center transition-all duration-1000 delay-700 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}>
                    <p className="text-gray-600 mb-6">Ready to get started?</p>
                    <div className="flex flex-col sm:flex-row gap-3 justify-center">
                        <a href="#pricing" className="btn-primary w-full sm:w-auto justify-center">
                            See pricing
                        </a>
                        <a href="#dual-audience" className="btn-secondary w-full sm:w-auto justify-center">
                            Learn more
                        </a>
                    </div>
                </div>
            </div>
        </section>
    );
}

export default HowItWorks;
